import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { apiAgent } from '../../../axiosConfig.js'
import { Popup } from '../../../design_system/components/Popup/Popup.jsx'
import { Loader } from '../../../design_system/components/Loader.jsx'
import ConditionalRender from '../../../ConditionalRender'
import { handleNewsImageError } from '../../utils/newsImageFallback.js'

const ArticleRow = ({ article, background, isLast = false }) => {
    const publishedDate = article.created_date || article.published_at
    const openArticle = () => {
        if (article.url) window.open(article.url, '_blank', 'noopener,noreferrer')
    }
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openArticle()
        }
    }
    return (
        <div
            className={`similar-articles__row similar-articles__row--${background}${isLast ? ' similar-articles__row--last' : ''}`}
            onClick={openArticle}
            onKeyDown={handleKeyDown}
            role="link"
            tabIndex={0}
        >
            {article.image_url && (
                <img src={article.image_url} alt="" className="similar-articles__row-image" onError={handleNewsImageError} />
            )}
            <div className="similar-articles__row-body">
                <a
                    href={article.url}
                    target="_blank"
                    rel="noreferrer"
                    className="body-m-bold similar-articles__row-title"
                    onClick={(e) => e.stopPropagation()}
                >
                    {article.title}
                </a>
                <div className="body-s-semibold similar-articles__row-description">{article.description || article.short_description}</div>
                <div className="similar-articles__row-source">
                    <a
                        href={`/news_source/${article.source}/feed`}
                        className="link-default"
                        onClick={(e) => e.stopPropagation()}
                    >{article.source}</a>
                    <ConditionalRender if={!!publishedDate}>
                        <span className="similar-articles__row-source-date"> | {publishedDate}</span>
                    </ConditionalRender>
                </div>
            </div>
        </div>
    )
}

export const SimilarArticlesPopup = ({
    articleId,
    masterArticle,
    prospectName: prospectNameProp,
    onClose,
}) => {
    const [data, setData] = useState(null)
    const [isFetching, setIsFetching] = useState(true)

    useEffect(() => {
        if (!articleId) return
        setIsFetching(true)
        apiAgent({
            method: 'get',
            url: `/aggregates/${articleId}/similar_articles`,
            params: {}
        }).then(res => {
            setData(res.data.data)
            setIsFetching(false)
        }).catch(() => setIsFetching(false))
    }, [articleId])

    const similarArticles = data?.similar_articles || []
    const prospectName = prospectNameProp || data?.news_query?.person_name
    const master = masterArticle || similarArticles[0]
    const others = masterArticle ? similarArticles : similarArticles.slice(1)
    const sourceCount = (master ? 1 : 0) + others.length

    const titleNode = (
        <div className="similar-articles__header-text">
            <div className="body-l-bold color-text-primary">Similar articles</div>
            <ConditionalRender if={!isFetching && sourceCount > 0 && !!prospectName}>
                <div className="similar-articles__header-subtitle">
                    {sourceCount} source{sourceCount !== 1 ? 's' : ''} published the same story about <span className="body-s-semibold color-text-primary">{prospectName}</span>
                </div>
            </ConditionalRender>
        </div>
    )

    return (
        <Popup
            name={titleNode}
            handleOnClosePopup={onClose}
            maxWidth={720}
            customHeaderStyle={{ alignItems: 'flex-start' }}
            customContentStyle={{ padding: 'var(--space-m) var(--space-xl) var(--space-s) var(--space-xl)' }}
            showFooterDivider={false}
        >
            <ConditionalRender if={isFetching}>
                <div className="similar-articles__loader-wrap"><Loader Size="Large" isLoading={true} /></div>
            </ConditionalRender>
            <ConditionalRender if={!isFetching}>
                <div className="similar-articles__sections">
                    <ConditionalRender if={!!master}>
                        <section className="similar-articles__section">
                            <div className="body-m-bold similar-articles__section-label">Master article</div>
                            {master && <ArticleRow article={master} background="subtle" />}
                        </section>
                    </ConditionalRender>
                    <ConditionalRender if={others.length > 0}>
                        <section className="similar-articles__section">
                            <div className="similar-articles__section-head">
                                <span className="body-m-bold similar-articles__section-label">Also featured in</span>
                                <span className="body-s-bold similar-articles__count-pill">{others.length}</span>
                            </div>
                            <div className="similar-articles__list">
                                {others.map((a, i) => (
                                    <ArticleRow
                                        key={a.url + i}
                                        article={a}
                                        background="page"
                                        isLast={i === others.length - 1}
                                    />
                                ))}
                            </div>
                        </section>
                    </ConditionalRender>
                </div>
            </ConditionalRender>
        </Popup>
    )
}

// Route-based wrapper — backwards compatible for /:article_id/similar_articles
const SimilarArticles = (props) => {
    const history = useHistory()
    const articleId = props.match?.params?.article_id
    const handleClose = () => {
        if (window.history.length > 1) history.goBack()
        else history.push('/')
    }
    if (!articleId) return null
    return <SimilarArticlesPopup articleId={articleId} onClose={handleClose} />
}

export default SimilarArticles
